import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import PriorityOrderField from "@/components/PriorityOrderField";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

// Leave rule editor. Supports both headcount and percentage
// concurrency caps; admin picks whichever they prefer (or both).

const LEAVE_TYPES = ["annual", "sick", "unpaid", "bank_holiday", "other"];

const EMPTY = {
  name: "", description: "", priority: 100, is_active: true,
  scope: "company", team_id: "", category_id: "",
  leave_types: [], max_concurrent_on_leave: null, max_concurrent_pct: null,
  min_notice_days: null, blackout_dates: [], allow_back_to_back: true,
  effective_from: "", effective_to: "",
  location_id: "",
  apply_per_team: false,
};

export default function LeaveRuleEditor({ open, onOpenChange, editing, categories, teams, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/locations").then(r => setLocations(r.data || [])).catch(() => setLocations([]));
    if (editing) {
      setForm({
        ...EMPTY,
        ...editing,
        scope: editing.apply_per_team && editing.scope !== "category"
                 ? "each_team"
                 : (editing.scope || "company"),  // rehydrate Each-team UI scope from apply_per_team
        team_id: editing.team_id || "",
        category_id: editing.category_id || "",
        blackout_dates: editing.blackout_dates || [],
        effective_from: editing.effective_from || "",
        effective_to: editing.effective_to || "",
        location_id: editing.location_id || "",
        apply_per_team: !!editing.apply_per_team,
      });
    } else { setForm(EMPTY); }
  }, [open, editing]);

  const set = (p) => setForm((prev) => ({ ...prev, ...p }));
  const toggleType = (t) => {
    const s = new Set(form.leave_types || []);
    if (s.has(t)) s.delete(t); else s.add(t);
    set({ leave_types: Array.from(s) });
  };
  const addBlackout = () =>
    set({ blackout_dates: [...form.blackout_dates, { start: "", end: "", label: "" }] });
  const updateBlackout = (i, patch) =>
    set({ blackout_dates: form.blackout_dates.map((b, idx) => idx === i ? { ...b, ...patch } : b) });
  const removeBlackout = (i) =>
    set({ blackout_dates: form.blackout_dates.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (form.scope === "team" && !form.team_id) return toast.error("Team is required");
    if (form.scope === "category" && !form.category_id) return toast.error("Category is required");
    setBusy(true);
    try {
      // "Each team" is a UI-side compound: backend scope stays
      // "company" but apply_per_team flips on. Mutually exclusive with
      // specific team_id (the UI already prevents that combination).
      const isEachTeam = form.scope === "each_team";
      const body = {
        name: form.name.trim(),
        description: form.description || null,
        priority: Number(form.priority) || 100,
        is_active: form.is_active,
        scope: isEachTeam ? "company" : form.scope,
        team_id: form.scope === "team" ? form.team_id : null,
        category_id: form.scope === "category" ? form.category_id : null,
        apply_per_team: isEachTeam || !!form.apply_per_team,
        leave_types: form.leave_types || [],
        max_concurrent_on_leave: form.max_concurrent_on_leave ?? null,
        max_concurrent_pct: form.max_concurrent_pct ?? null,
        min_notice_days: form.min_notice_days ?? null,
        blackout_dates: (form.blackout_dates || []).filter((b) => b.start && b.end),
        allow_back_to_back: !!form.allow_back_to_back,
        effective_from: form.effective_from || null,
        effective_to: form.effective_to || null,
        location_id: form.location_id || null,
      };
      if (editing) {
        await api.patch(`/leave-rules/${editing.leave_rule_id}`, body);
        toast.success("Leave policy updated");
      } else {
        await api.post("/leave-rules", body);
        toast.success("Leave policy created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" fullscreenOnMobile data-testid="leave-editor-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit leave policy" : "New leave policy"}</DialogTitle>
          <DialogDescription>
            Set the rules for how leave is managed, limits on overlapping leave,
            advance notice, blackout dates, and per-team or per-category caps.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="eyebrow">Name</Label>
              <Input required value={form.name} data-testid="leave-name"
                onChange={(e) => set({ name: e.target.value })}
                className="h-11 rounded-lg mt-2" />
            </div>
            <div>
              <Label className="eyebrow">Scope</Label>
              <Select value={form.apply_per_team && form.scope !== "category" ? "each_team" : form.scope}
                       onValueChange={(v) => {
                         if (v === "each_team") set({ scope: "each_team", apply_per_team: true, team_id: "" });
                         else set({ scope: v, apply_per_team: false });
                       }}>
                <SelectTrigger className="h-11 rounded-lg mt-2"
                  data-testid="leave-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company" data-testid="leave-scope-opt-company">Company-wide</SelectItem>
                  <SelectItem value="team" data-testid="leave-scope-opt-team">Specific team</SelectItem>
                  <SelectItem value="each_team" data-testid="leave-scope-opt-each-team">Each team (one rule, evaluated per team)</SelectItem>
                  <SelectItem value="category" data-testid="leave-scope-opt-category">Category</SelectItem>
                </SelectContent>
              </Select>
              {(form.scope === "each_team" || form.apply_per_team) && (
                <p className="text-xs text-[#4B5773] mt-2"
                   data-testid="leave-scope-each-team-helper">
                  This rule is evaluated separately for every team using each
                  team's own employees. You do not need to create one rule per
                  team.
                </p>
              )}
            </div>
            <div>
              <PriorityOrderField
                value={form.priority}
                onChange={(v) => set({ priority: v })}
                testid="leave-priority" />
            </div>
            {form.scope === "team" && (
              <div className="col-span-2">
                <Label className="eyebrow">Team</Label>
                <Select value={form.team_id} onValueChange={(v) => set({ team_id: v })}>
                  <SelectTrigger className="h-11 rounded-lg mt-2"
                    data-testid="leave-team"><SelectValue placeholder="Pick a team" /></SelectTrigger>
                  <SelectContent>
                    {teams.map((t) => (
                      <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.scope === "category" && (
              <div className="col-span-2">
                <Label className="eyebrow">Category</Label>
                <Select value={form.category_id} onValueChange={(v) => set({ category_id: v })}>
                  <SelectTrigger className="h-11 rounded-lg mt-2"
                    data-testid="leave-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                  <SelectContent>
                    {categories.filter((c) => c.is_active !== false).map((c) => (
                      <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {locations.length > 0 && (
              <div className="col-span-2" data-testid="leave-location-row">
                <Label className="eyebrow">Location (optional)</Label>
                <Select value={form.location_id || "__all__"}
                  onValueChange={(v) => set({ location_id: v === "__all__" ? "" : v })}>
                  <SelectTrigger className="h-11 rounded-lg mt-2"
                    data-testid="leave-location"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All locations</SelectItem>
                    {locations.map((l) => (
                      <SelectItem key={l.location_id} value={l.location_id}
                                   data-testid={`leave-location-opt-${l.location_id}`}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <Label className="eyebrow">Which leave types this rule applies to</Label>
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="leave-types">
              {LEAVE_TYPES.map((t) => {
                const on = (form.leave_types || []).includes(t);
                return (
                  <button type="button" key={t} onClick={() => toggleType(t)}
                    data-testid={`leave-type-${t}`}
                    className={`text-xs px-2.5 py-1 rounded-full border ${
                      on
                        ? "bg-[#2563EB] text-white border-[#2563EB]"
                        : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                    }`}>{t}</button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#4B5773] mt-1.5">Leave empty to apply to all leave types.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="eyebrow">Maximum people off at the same time</Label>
              <Input type="number" min="0" data-testid="leave-max-headcount"
                value={form.max_concurrent_on_leave ?? ""}
                onChange={(e) => set({
                  max_concurrent_on_leave: e.target.value === "" ? null : Number(e.target.value),
                })}
                className="h-11 rounded-lg mt-2" placeholder="No limit" />
            </div>
            <div>
              <Label className="eyebrow">…or as a percentage of the team (%)</Label>
              <Input type="number" min="0" max="100" data-testid="leave-max-pct"
                value={form.max_concurrent_pct ?? ""}
                onChange={(e) => set({
                  max_concurrent_pct: e.target.value === "" ? null : Number(e.target.value),
                })}
                className="h-11 rounded-lg mt-2" placeholder="No limit" />
            </div>
            <div>
              <Label className="eyebrow">Minimum notice required (days)</Label>
              <Input type="number" min="0" data-testid="leave-min-notice"
                value={form.min_notice_days ?? ""}
                onChange={(e) => set({
                  min_notice_days: e.target.value === "" ? null : Number(e.target.value),
                })}
                className="h-11 rounded-lg mt-2" />
              <p className="text-[11px] text-[#4B5773] mt-1">
                How far in advance employees must request this type of leave.
              </p>
            </div>
            <label className="flex items-end gap-2 text-xs text-[#4B5773] pb-2">
              <input type="checkbox" className="accent-[#2563EB]"
                data-testid="leave-allow-back-to-back"
                checked={!!form.allow_back_to_back}
                onChange={(e) => set({ allow_back_to_back: e.target.checked })} />
              Allow back-to-back requests
            </label>
          </div>

          <div className="border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="eyebrow">Blackout dates</div>
              <Button type="button" onClick={addBlackout}
                data-testid="leave-blackout-add"
                className="rounded-md bg-[#2563EB] hover:bg-[#1D4ED8] h-8 text-xs">
                <Plus className="h-3.5 w-3.5 mr-1" />Add
              </Button>
            </div>
            {(form.blackout_dates || []).length === 0 && (
              <p className="text-xs text-[#4B5773]" data-testid="leave-blackouts-empty">
                No blackout dates yet, add periods like Christmas week, peak trading days or system go-lives when leave is restricted.
              </p>
            )}
            <ul className="space-y-2" data-testid="leave-blackouts-list">
              {(form.blackout_dates || []).map((b, i) => (
                <li key={i} className="grid grid-cols-12 gap-2 items-center"
                    data-testid={`leave-blackout-row-${i}`}>
                  <Input type="date" value={b.start}
                    onChange={(e) => updateBlackout(i, { start: e.target.value })}
                    data-testid={`leave-blackout-start-${i}`}
                    className="col-span-4 h-9 rounded-lg" />
                  <Input type="date" value={b.end}
                    onChange={(e) => updateBlackout(i, { end: e.target.value })}
                    data-testid={`leave-blackout-end-${i}`}
                    className="col-span-4 h-9 rounded-lg" />
                  <Input value={b.label || ""}
                    onChange={(e) => updateBlackout(i, { label: e.target.value })}
                    placeholder="Label"
                    data-testid={`leave-blackout-label-${i}`}
                    className="col-span-3 h-9 rounded-lg" />
                  <Button type="button" variant="ghost" size="sm"
                    data-testid={`leave-blackout-remove-${i}`}
                    className="col-span-1 h-9 w-9 p-0"
                    onClick={() => removeBlackout(i)}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="eyebrow">Effective from</Label>
              <Input type="date" value={form.effective_from} data-testid="leave-eff-from"
                onChange={(e) => set({ effective_from: e.target.value })}
                className="h-11 rounded-lg mt-2" />
            </div>
            <div>
              <Label className="eyebrow">Effective to</Label>
              <Input type="date" value={form.effective_to} data-testid="leave-eff-to"
                onChange={(e) => set({ effective_to: e.target.value })}
                className="h-11 rounded-lg mt-2" />
            </div>
          </div>

          <div>
            <Label className="eyebrow">Description (optional)</Label>
            <Input value={form.description} data-testid="leave-description"
              onChange={(e) => set({ description: e.target.value })}
              className="h-11 rounded-lg mt-2" />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="leave-submit"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              {busy ? "Saving…" : editing ? "Save changes" : "Create policy"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
